// frontend/src/components/bd/bubbles/BubbleEditor.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { buildBubbleSvg, boxHeightPx, FONT_FAMILY, fontSizeFor, BD_W, BD_H, MAX_H_FRAC } from "./svgBuilder";
import { fitFontSize, canvasMeasurer, wrapText } from "./wrap";

const DEFAULT_W = 0.4; // largeur fraction d'une nouvelle bulle
const FONT_SLIDER_MIN = 14;
const FONT_SLIDER_MAX = 48;

// Largeur de boîte en px (pour le wrap) à partir de la fraction w.
const boxPx = (wFrac) => wFrac * BD_W - 2 * 16; // PAD_X = 16 des deux côtés

// Miroir du validator _clamp_box de geometry.py : la boîte reste entièrement
// dans l'image (w ≤ 0.95, x+w ≤ 1, y+h ≤ 1, jamais < 0). On déplace, on ne
// rétrécit pas. Helper pur, exporté pour les tests.
export function clampBox(b) {
  if (b.w > 0.95) b.w = 0.95;
  if (b.x + b.w > 1.0) b.x = Math.max(0.0, 1.0 - b.w);
  const h = boxHeightPx((b.lines || []).length, b.kind, b.font_size) / BD_H;
  if (b.y + h > 1.0) b.y = Math.max(0.0, 1.0 - h);
  return b;
}

// Bornes de largeur d'une bulle (fraction de la largeur image). Mêmes valeurs
// que le slider « Largeur » du panneau d'édition (input range min=0.15 max=0.95).
export const W_MIN = 0.15;
export const W_MAX = 0.95;

// Largeur de boîte depuis la position horizontale du curseur (fraction image),
// coin haut-gauche ancré en boxX : le bord droit suit le curseur. Bornée à
// [W_MIN, W_MAX] ; clampBox affine ensuite (x + w ≤ 1). Helper pur, exporté
// pour les tests.
export function resizeWidth(fracX, boxX) {
  const w = fracX - boxX;
  if (w < W_MIN) return W_MIN;
  if (w > W_MAX) return W_MAX;
  return w;
}

export function reflowBubble(b, measureFactory) {
  if (b.font_locked) {
    const fontSize = b.font_size != null ? b.font_size : fontSizeFor(b.kind);
    const measure = measureFactory(fontSize);
    const hasWords = String(b.text).split(/\s+/).filter(Boolean).length > 0;
    const lines = measure ? wrapText(b.text, boxPx(b.w), measure) : (hasWords ? [String(b.text)] : [""]);
    return { ...b, lines, font_size: fontSize, font_locked: true };
  }
  const { fontSize, lines } = fitFontSize(
    b.text, b.kind, boxPx(b.w), MAX_H_FRAC * BD_H, measureFactory,
  );
  return { ...b, lines, font_size: fontSize };
}

export default function BubbleEditor({ src, initialLayout, onSave, onCancel, busy }) {
  // Gate de chargement de police : aucune mesure canvas ne se fait avant que
  // Patrick Hand soit effectivement disponible dans le navigateur. Dégradé :
  // si le chargement échoue (catch), on passe quand même à true pour ne pas
  // bloquer l'éditeur.
  const [fontReady, setFontReady] = useState(false);
  useEffect(() => {
    let alive = true;
    const probes = [
      `${fontSizeFor("speech")}px '${FONT_FAMILY}'`,
      `${fontSizeFor("narration")}px '${FONT_FAMILY}'`,
    ];
    Promise.all(probes.map((p) => document.fonts.load(p)))
      .then(() => document.fonts.ready)
      .then(() => { if (alive) setFontReady(true); })
      .catch(() => { if (alive) setFontReady(true); }); // dégradé : ne pas bloquer
    return () => { alive = false; };
  }, []);

  // Factory de mesureurs canvas par taille de police — recréée APRÈS chargement
  // de la police (fontReady en dépendance) pour refléter les vraies largeurs
  // Patrick Hand. L'auto-fit teste plusieurs tailles, d'où la factory.
  const measureFactory = useMemo(
    () => (size) => canvasMeasurer(size, FONT_FAMILY),
    [fontReady], // eslint-disable-line
  );
  const reflow = (b) => reflowBubble(b, measureFactory);
  // Initialisation sans reflow (police pas encore chargée) : on stocke les
  // bulles telles qu'elles arrivent et on attend fontReady pour le premier
  // reflow correct.
  const [bubbles, setBubbles] = useState(() => initialLayout || []);

  // Reflow de toutes les bulles dès que la police est prête (ou si elle était
  // déjà prête au montage — cas cache navigateur). Garantit que les lignes
  // calculées correspondent aux largeurs réelles de Patrick Hand.
  useEffect(() => {
    if (!fontReady) return;
    setBubbles((bs) => bs.map((b) => reflow({ ...b })));
  }, [fontReady]); // eslint-disable-line

  const [selected, setSelected] = useState(null); // id
  const wrapRef = useRef(null);
  const drag = useRef(null); // { id, mode, move, up }

  useEffect(
    () => () => {
      if (drag.current) {
        window.removeEventListener("mousemove", drag.current.move);
        window.removeEventListener("mouseup", drag.current.up);
      }
    },
    [],
  );

  const updateBubble = (id, patch, clamp = false) =>
    setBubbles((bs) =>
      bs.map((b) => {
        if (b.id !== id) return b;
        const nb = reflow({ ...b, ...patch });
        return clamp ? clampBox(nb) : nb;
      }),
    );

  // coordonnées image (0–1) depuis un évènement souris
  const toFrac = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    return {
      fx: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      fy: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };

  // clic dans l'image : si une bulle speech est sélectionnée, déplacer sa pointe
  const onImageClick = (e) => {
    if (!selected) return;
    const b = bubbles.find((x) => x.id === selected);
    if (!b || b.kind !== "speech") return;
    const { fx, fy } = toFrac(e);
    updateBubble(selected, { tail_x: fx, tail_y: fy });
  };

  const startDrag = (e, id, mode) => {
    e.stopPropagation();
    // Coin haut-gauche ancré pour le resize : on capture x au début du geste
    // (x ne bouge pas pendant un redimensionnement, seul le bord droit suit).
    const found = bubbles.find((b) => b.id === id);
    const boxX = found ? found.x : 0;
    const move = (ev) => {
      if (!drag.current) return;
      const { fx, fy } = toFrac(ev);
      if (drag.current.mode === "box") updateBubble(drag.current.id, { x: fx, y: fy }, true);
      else if (drag.current.mode === "resize") updateBubble(drag.current.id, { w: resizeWidth(fx, boxX) }, true);
      else updateBubble(drag.current.id, { tail_x: fx, tail_y: fy });
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      drag.current = null;
    };
    drag.current = { id, mode, move, up };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const addBubble = (kind) => {
    const id = Math.random().toString(36).slice(2, 10);
    const base = { id, text: "Texte", speaker: kind === "narration" ? "narration" : "",
                   kind, x: 0.1, y: 0.1, w: DEFAULT_W, font_locked: false };
    const b = kind === "speech" ? { ...base, tail_x: 0.3, tail_y: 0.4 }
                                : { ...base, tail_x: null, tail_y: null };
    setBubbles((bs) => [...bs, clampBox(reflow(b))]);
    setSelected(id);
  };
  const removeBubble = (id) => {
    setBubbles((bs) => bs.filter((b) => b.id !== id));
    if (selected === id) setSelected(null);
  };
  const toggleKind = (id) => {
    const b = bubbles.find((x) => x.id === id);
    if (!b) return;
    if (b.kind === "speech") updateBubble(id, { kind: "narration", tail_x: null, tail_y: null });
    else updateBubble(id, { kind: "speech", tail_x: Math.min(0.98, b.x + b.w / 2), tail_y: Math.min(0.98, b.y + 0.2) });
  };

  const svg = buildBubbleSvg(bubbles, BD_W, BD_H);
  const sel = bubbles.find((b) => b.id === selected);
  const selFontSize = sel ? (sel.font_size != null ? sel.font_size : fontSizeFor(sel.kind)) : null;

  return (
    <div className="mt-2 border rounded p-2 bg-gray-50">
      {!sel && (
        <p className="text-xs text-gray-600 mb-1">
          👉 Clique sur une bulle pour <strong>modifier son texte</strong> ou la{" "}
          <strong>supprimer</strong>. Glisse le point pour la déplacer.
        </p>
      )}
      <div ref={wrapRef} onClick={onImageClick}
           style={{ position: "relative", width: "100%", maxWidth: 512, cursor: selected ? "crosshair" : "default" }}>
        <img src={src} alt="planche" style={{ width: "100%", display: "block" }} />
        {/* overlay SVG : rendu exact via le builder partagé */}
        <div style={{ position: "absolute", inset: 0 }}
             dangerouslySetInnerHTML={{ __html: svg.replace("<svg ", '<svg style="width:100%;height:100%" ') }} />
        {/* zone cliquable couvrant TOUTE la bulle (pas seulement le point) :
            sélectionne la bulle → ouvre le panneau texte + Supprimer. Contour
            pointillé = repère visuel « c'est cliquable », plein = sélectionnée. */}
        {bubbles.map((b) => (
          <button key={`hit-${b.id}`} title="modifier ou supprimer cette bulle"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setSelected(b.id); }}
            style={{ position: "absolute", left: `${b.x * 100}%`, top: `${b.y * 100}%`,
                     width: `${b.w * 100}%`,
                     height: `${(boxHeightPx((b.lines || []).length, b.kind, b.font_size) / BD_H) * 100}%`,
                     background: "transparent", padding: 0,
                     borderRadius: b.kind === "speech" ? 18 : 4,
                     border: selected === b.id ? "2px solid #1f6feb" : "1px dashed rgba(31,111,235,0.45)",
                     cursor: "pointer" }} />
        ))}
        {/* poignées de drag (boîte + pointe) au-dessus de l'overlay */}
        {bubbles.map((b) => (
          <React.Fragment key={b.id}>
            <button title="déplacer la bulle"
              onMouseDown={(e) => startDrag(e, b.id, "box")}
              onClick={(e) => { e.stopPropagation(); setSelected(b.id); }}
              style={{ position: "absolute", left: `${b.x * 100}%`, top: `${b.y * 100}%`,
                       width: 14, height: 14, borderRadius: 7,
                       background: selected === b.id ? "#1f6feb" : "#888", border: "2px solid #fff", cursor: "move" }} />
            {b.kind === "speech" && b.tail_x != null && (
              <button title="déplacer la pointe"
                onMouseDown={(e) => startDrag(e, b.id, "tail")}
                style={{ position: "absolute", left: `${b.tail_x * 100}%`, top: `${b.tail_y * 100}%`,
                         width: 12, height: 12, borderRadius: 6,
                         background: "#e3a008", border: "2px solid #fff", cursor: "move" }} />
            )}
            {selected === b.id && (
              <button title="redimensionner la largeur — étire le coin"
                onMouseDown={(e) => startDrag(e, b.id, "resize")}
                onClick={(e) => e.stopPropagation()}
                style={{ position: "absolute",
                         left: `${(b.x + b.w) * 100}%`,
                         top: `${(b.y + boxHeightPx((b.lines || []).length, b.kind, b.font_size) / BD_H) * 100}%`,
                         width: 12, height: 12, borderRadius: 2,
                         transform: "translate(-50%, -50%)",
                         background: "#1f6feb", border: "2px solid #fff", cursor: "ew-resize" }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* panneau d'édition de la bulle sélectionnée */}
      {sel && (
        <div className="mt-2 text-xs space-y-1">
          <textarea value={sel.text} rows={2} className="w-full border rounded p-1 font-mono"
            onChange={(e) => updateBubble(sel.id, { text: e.target.value })} />
          <div className="flex gap-2 items-center">
            <button className="px-2 py-1 bg-gray-200 rounded" onClick={() => toggleKind(sel.id)}>
              {sel.kind === "speech" ? "→ narration" : "→ dialogue"}
            </button>
            <label>Largeur
              <input type="range" min="0.15" max="0.95" step="0.01" value={sel.w}
                onChange={(e) => updateBubble(sel.id, { w: parseFloat(e.target.value) }, true)} />
            </label>
            <label>Police {selFontSize}px {sel.font_locked ? "(manuel)" : "(auto)"}
              <input type="range" min={FONT_SLIDER_MIN} max={FONT_SLIDER_MAX} step="1" value={selFontSize}
                onChange={(e) => updateBubble(sel.id, {
                  font_size: parseInt(e.target.value, 10),
                  font_locked: true,
                }, true)} />
            </label>
            {sel.font_locked && (
              <button className="px-2 py-1 bg-gray-200 rounded" title="Revenir à la taille automatique"
                onClick={() => updateBubble(sel.id, { font_locked: false }, true)}>
                Auto
              </button>
            )}
            <button className="px-2 py-1 bg-red-600 text-white rounded font-medium" onClick={() => removeBubble(sel.id)}>
              🗑 Supprimer
            </button>
          </div>
          {sel.kind === "speech" && (
            <p className="text-gray-500">Clique sur le personnage qui parle pour diriger la pointe.</p>
          )}
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <button className="px-2 py-1 bg-blue-100 rounded text-xs" onClick={() => addBubble("speech")}>+ bulle</button>
        <button className="px-2 py-1 bg-blue-100 rounded text-xs" onClick={() => addBubble("narration")}>+ narration</button>
        <span className="flex-1" />
        <button disabled={busy} className="px-3 py-1 bg-green-600 text-white rounded text-xs disabled:opacity-50"
          onClick={() => onSave(bubbles)}>{busy ? "…" : "Enregistrer"}</button>
        <button disabled={busy} className="px-3 py-1 bg-gray-200 rounded text-xs" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}
